import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { Song, Setlist } from '../types/song';
import { transposeChordProContent, getTransposedKey } from '../services/transposeService';
import { 
  importChordPro as importChordProService,
  importFromUltimateGuitar as importFromUGService,
  importFromUrl as importFromUrlService,
  exportSongs as exportSongsService,
  importSongsFromJson,
  isAsyncImportResult,
  pollJobStatus
} from '../services/importExportService';
import { 
  getStorageService, 
  NotFoundError, 
  StorageQuotaExceededError, 
  ConflictError,
  StorageError 
} from '../services/storageService';

interface ImportingJob {
  jobId: string;
  url: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error?: string;
  createdAt: string;
}

interface SongStore {
  // State
  songs: Song[];
  setlists: Setlist[];
  currentSong: Song | null;
  currentSetlist: Setlist | null;
  selectedTransposition: number;
  isLoading: boolean;
  error: string | null;
  importingJobs: ImportingJob[];
  hasLoadedInitial: boolean;
  
  // Initialization
  loadSongs: () => Promise<void>;
  
  // Song Management
  addSong: (song: Omit<Song, 'id' | 'createdAt' | 'lastModified'>) => Promise<string>;
  updateSong: (id: string, updates: Partial<Song>) => Promise<void>;
  deleteSong: (id: string) => Promise<void>;
  getSong: (id: string) => Song | undefined;
  searchSongs: (query: string) => Song[];
  
  // Tag Management
  addTag: (songId: string, tag: string) => Promise<void>;
  removeTag: (songId: string, tag: string) => Promise<void>;
  getAllTags: () => string[];
  
  // Transposition
  transposeSong: (songId: string, semitones: number) => Song | null;
  setTransposition: (semitones: number) => void;
  resetTransposition: () => void;
  
  // Import Functions
  importChordPro: (content: string, metadata?: Partial<Song>) => Promise<string>;
  importFromUltimateGuitar: (content: string, metadata?: Partial<Song>) => Promise<string>;
  importFromUrl: (url: string, useAsync?: boolean) => Promise<string>;
  
  // Import Job Management
  addImportingJob: (jobId: string, url: string) => void;
  updateImportingJob: (jobId: string, updates: Partial<ImportingJob>) => void;
  removeImportingJob: (jobId: string) => void;
  getImportingJobs: () => ImportingJob[];
  
  // Setlist Management
  createSetlist: (name: string, description?: string) => Promise<string>;
  updateSetlist: (id: string, updates: Partial<Setlist>) => Promise<void>;
  deleteSetlist: (id: string) => Promise<void>;
  addSongToSetlist: (setlistId: string, songId: string) => Promise<void>;
  removeSongFromSetlist: (setlistId: string, songId: string) => Promise<void>;
  reorderSetlistSongs: (setlistId: string, fromIndex: number, toIndex: number) => Promise<void>;
  
  // Current Selection
  setCurrentSong: (song: Song | null) => void;
  setCurrentSetlist: (setlist: Setlist | null) => void;
  
  // Utility
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  exportSongs: () => Promise<string>;
  importSongs: (data: string) => Promise<void>;
}

export const useSongStoreWithPersistence = create<SongStore>((set, get) => ({
  // Initial state
  songs: [],
  setlists: [],
  currentSong: null,
  currentSetlist: null,
  selectedTransposition: 0,
  isLoading: false,
  error: null,
  importingJobs: [],
  hasLoadedInitial: false,

  // Initialization - load songs from storage service
  loadSongs: async () => {
    const storage = getStorageService();
    set({ isLoading: true, error: null });
    
    try {
      const [songs, setlists] = await Promise.all([
        storage.getSongs(),
        storage.getSetlists()
      ]);
      
      set({ songs, setlists, isLoading: false, hasLoadedInitial: true });
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Failed to load songs',
        hasLoadedInitial: true 
      });
    }
  },

  // Song Management
  addSong: async (songData) => {
    const storage = getStorageService();
    const id = uuidv4();
    const now = new Date().toISOString();
    const song: Song = {
      ...songData,
      id,
      createdAt: now,
      lastModified: now,
    };
    
    set({ isLoading: true, error: null });
    
    try {
      await storage.saveSong(song);
      set(state => ({ 
        songs: [...state.songs, song],
        isLoading: false 
      }));
      return id;
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Failed to save song' 
      });
      throw error;
    }
  },

  updateSong: async (id, updates) => {
    const storage = getStorageService();
    set({ isLoading: true, error: null });
    
    try {
      await storage.updateSong(id, updates);
      set(state => ({
        songs: state.songs.map(song =>
          song.id === id
            ? { ...song, ...updates, lastModified: new Date().toISOString() }
            : song
        ),
        currentSong: state.currentSong?.id === id
          ? { ...state.currentSong, ...updates, lastModified: new Date().toISOString() }
          : state.currentSong,
        isLoading: false
      }));
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Failed to update song' 
      });
      throw error;
    }
  },

  deleteSong: async (id) => {
    const storage = getStorageService();
    set({ isLoading: true, error: null });
    
    try {
      await storage.deleteSong(id);
      set(state => ({
        songs: state.songs.filter(song => song.id !== id),
        currentSong: state.currentSong?.id === id ? null : state.currentSong,
        // Remove from all setlists in memory
        setlists: state.setlists.map(setlist => ({
          ...setlist,
          songs: setlist.songs.filter(songId => songId !== id)
        })),
        isLoading: false
      }));
    } catch (error) {
      // If song not found on server, still remove from local state
      if (error instanceof NotFoundError) {
        set(state => ({
          songs: state.songs.filter(song => song.id !== id),
          currentSong: state.currentSong?.id === id ? null : state.currentSong,
          setlists: state.setlists.map(setlist => ({
            ...setlist,
            songs: setlist.songs.filter(songId => songId !== id)
          })),
          isLoading: false
        }));
        // Don't throw the error - deletion succeeded from user's perspective
        return;
      }
      
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Failed to delete song' 
      });
      throw error;
    }
  },

  getSong: (id) => {
    return get().songs.find(song => song.id === id);
  },

  searchSongs: (query) => {
    const lowerQuery = query.toLowerCase();
    return get().songs.filter(song =>
      song.title.toLowerCase().includes(lowerQuery) ||
      song.artist.toLowerCase().includes(lowerQuery) ||
      song.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
    );
  },

  // Tag Management
  addTag: async (songId, tag) => {
    const song = get().songs.find(s => s.id === songId);
    if (song && !song.tags.includes(tag)) {
      await get().updateSong(songId, { tags: [...song.tags, tag] });
    }
  },

  removeTag: async (songId, tag) => {
    const song = get().songs.find(s => s.id === songId);
    if (song) {
      await get().updateSong(songId, { tags: song.tags.filter(t => t !== tag) });
    }
  },

  getAllTags: () => {
    const allTags = get().songs.flatMap(song => song.tags);
    return [...new Set(allTags)];
  },

  // Transposition
  transposeSong: (songId, semitones) => {
    const song = get().songs.find(s => s.id === songId);
    if (!song) return null;

    const transposedContent = transposeChordProContent(song.chordProContent, semitones);
    const transposedKey = getTransposedKey(song.originalKey, semitones);

    const transposedSong = {
      ...song,
      chordProContent: transposedContent,
      key: transposedKey
    };

    // Update current song if it's the one being transposed
    if (get().currentSong?.id === songId) {
      set({ currentSong: transposedSong });
    }

    return transposedSong;
  },

  setTransposition: (semitones) => {
    set({ selectedTransposition: semitones });
    const currentSong = get().currentSong;
    if (currentSong) {
      get().transposeSong(currentSong.id, semitones);
    }
  },

  resetTransposition: () => {
    set({ selectedTransposition: 0 });
    const currentSong = get().currentSong;
    if (currentSong) {
      const originalSong = get().songs.find(s => s.id === currentSong.id);
      if (originalSong) {
        set({ currentSong: originalSong });
      }
    }
  },

  // Import Functions
  importChordPro: async (content, metadata = {}) => {
    set({ isLoading: true, error: null });
    
    try {
      const result = await importChordProService(content, metadata);
      
      const song = {
        title: result.title,
        artist: result.artist,
        key: result.key,
        originalKey: result.key,
        chordProContent: result.chordPro,
        tags: metadata.tags || [],
        isPublic: metadata.isPublic || false,
        createdBy: metadata.createdBy || 'user',
        tempo: result.tempo,
        metadata: {
          source: 'import' as const,
          capo: result.capo,
          ...metadata.metadata
        }
      };

      set({ isLoading: false });
      return get().addSong(song);
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Import failed' 
      });
      throw error;
    }
  },

  importFromUltimateGuitar: async (content, metadata = {}) => {
    set({ isLoading: true, error: null });
    
    try {
      const result = await importFromUGService(content, metadata);
      
      const song = {
        title: result.title,
        artist: result.artist,
        key: result.key,
        originalKey: result.key,
        chordProContent: result.chordPro,
        tags: metadata.tags || ['imported', 'ultimate-guitar'],
        isPublic: metadata.isPublic || false,
        createdBy: metadata.createdBy || 'user',
        tempo: result.tempo,
        metadata: {
          source: 'ultimate-guitar' as const,
          capo: result.capo,
          ...metadata.metadata
        }
      };

      set({ isLoading: false });
      return get().addSong(song);
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Import failed' 
      });
      throw error;
    }
  },
  
  importFromUrl: async (url, useAsync = true) => {
    // Always use async mode for URL imports to ensure proper conversion
    useAsync = true;
    set({ isLoading: true, error: null });
    
    try {
      const result = await importFromUrlService(url, useAsync);
      
      // Check if this is an async job
      if (isAsyncImportResult(result)) {
        // Add the importing job to track it
        get().addImportingJob(result.jobId, url);
        
        // Start polling in the background
        pollJobStatus(result.jobId, (status) => {
          console.log('Import job status:', status);
          
          // Update job status
          get().updateImportingJob(result.jobId, {
            status: status.status === 'completed' || status.status === 'failed' 
              ? status.status 
              : 'processing',
            error: status.error
          });
          
          if (status.status === 'completed' && status.result) {
            // Debug log the result structure
            console.log('Job completed with result:', {
              hasResult: !!status.result,
              hasChordPro: !!status.result.chordPro,
              chordProLength: status.result.chordPro?.length,
              hasMetadata: !!status.result.metadata,
              metadataKeys: status.result.metadata ? Object.keys(status.result.metadata) : [],
              fullResult: status.result
            });
            
            // Import the song
            const song = {
              title: status.result.metadata.title || 'Untitled',
              artist: status.result.metadata.artist || 'Unknown Artist',
              key: status.result.metadata.key || 'C',
              originalKey: status.result.metadata.key || 'C',
              chordProContent: status.result.chordPro,
              tags: ['imported', 'url'],
              isPublic: false,
              createdBy: 'user',
              tempo: status.result.metadata.tempo,
              metadata: {
                source: 'url' as const,
                sourceUrl: url,
                capo: status.result.metadata.capo,
                ...status.result.metadata
              }
            };
            
            console.log('Creating song object:', {
              title: song.title,
              artist: song.artist,
              hasChordProContent: !!song.chordProContent,
              chordProContentLength: song.chordProContent?.length,
              firstLine: song.chordProContent?.split('\n')[0]
            });
            
            // Add the song
            get().addSong(song).then(() => {
              // Remove the job
              get().removeImportingJob(result.jobId);
            }).catch((error: unknown) => {
              console.error('Failed to add imported song:', error);
              get().updateImportingJob(result.jobId, {
                status: 'failed',
                error: error instanceof Error ? error.message : 'Failed to save song'
              });
            });
          } else if (status.status === 'failed') {
            // Mark job as failed but keep it visible
            console.error('Import job failed:', status.error);
          }
        }).catch((error: unknown) => {
          console.error('Polling failed:', error);
          get().updateImportingJob(result.jobId, {
            status: 'failed',
            error: error instanceof Error ? error.message : 'Polling failed'
          });
        });
        
        set({ isLoading: false });
        return result.jobId;
      }
      
      // Synchronous result (shouldn't happen with useAsync=true)
      const song = {
        title: result.title,
        artist: result.artist,
        key: result.key,
        originalKey: result.key,
        chordProContent: result.chordPro,
        tags: ['imported', 'url'],
        isPublic: false,
        createdBy: 'user',
        tempo: result.tempo,
        metadata: {
          source: 'url' as const,
          sourceUrl: url,
          capo: result.capo
        }
      };

      set({ isLoading: false });
      return get().addSong(song);
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Import from URL failed' 
      });
      throw error;
    }
  },

  // Import Job Management  
  addImportingJob: (jobId, url) => {
    const job: ImportingJob = {
      jobId,
      url,
      status: 'pending',
      createdAt: new Date().toISOString()
    };
    set(state => ({ importingJobs: [...state.importingJobs, job] }));
  },

  updateImportingJob: (jobId, updates) => {
    set(state => ({
      importingJobs: state.importingJobs.map(job =>
        job.jobId === jobId ? { ...job, ...updates } : job
      )
    }));
  },

  removeImportingJob: (jobId) => {
    set(state => ({
      importingJobs: state.importingJobs.filter(job => job.jobId !== jobId)
    }));
  },

  getImportingJobs: () => {
    return get().importingJobs;
  },

  // Setlist Management
  createSetlist: async (name, description) => {
    const storage = getStorageService();
    const id = uuidv4();
    const now = new Date().toISOString();
    const setlist: Setlist = {
      id,
      name,
      description,
      songs: [],
      createdAt: now,
      lastModified: now
    };
    
    try {
      await storage.saveSetlist(setlist);
      set(state => ({ setlists: [...state.setlists, setlist] }));
      return id;
    } catch (error) {
      // If setlists not implemented, store locally
      if (error instanceof StorageError && error.code === 'NOT_IMPLEMENTED') {
        set(state => ({ setlists: [...state.setlists, setlist] }));
        return id;
      }
      throw error;
    }
  },

  updateSetlist: async (id, updates) => {
    const storage = getStorageService();
    
    try {
      await storage.updateSetlist(id, updates);
      set(state => ({
        setlists: state.setlists.map(setlist =>
          setlist.id === id
            ? { ...setlist, ...updates, lastModified: new Date().toISOString() }
            : setlist
        )
      }));
    } catch (error) {
      // If setlists not implemented, update locally
      if (error instanceof StorageError && error.code === 'NOT_IMPLEMENTED') {
        set(state => ({
          setlists: state.setlists.map(setlist =>
            setlist.id === id
              ? { ...setlist, ...updates, lastModified: new Date().toISOString() }
              : setlist
          )
        }));
        return;
      }
      throw error;
    }
  },

  deleteSetlist: async (id) => {
    const storage = getStorageService();
    
    try {
      await storage.deleteSetlist(id);
      set(state => ({
        setlists: state.setlists.filter(setlist => setlist.id !== id),
        currentSetlist: state.currentSetlist?.id === id ? null : state.currentSetlist
      }));
    } catch (error) {
      // If setlists not implemented, delete locally
      if (error instanceof StorageError && error.code === 'NOT_IMPLEMENTED') {
        set(state => ({
          setlists: state.setlists.filter(setlist => setlist.id !== id),
          currentSetlist: state.currentSetlist?.id === id ? null : state.currentSetlist
        }));
        return;
      }
      throw error;
    }
  },

  addSongToSetlist: async (setlistId, songId) => {
    const setlist = get().setlists.find(s => s.id === setlistId);
    if (setlist && !setlist.songs.includes(songId)) {
      await get().updateSetlist(setlistId, {
        songs: [...setlist.songs, songId]
      });
    }
  },

  removeSongFromSetlist: async (setlistId, songId) => {
    const setlist = get().setlists.find(s => s.id === setlistId);
    if (setlist) {
      await get().updateSetlist(setlistId, {
        songs: setlist.songs.filter(id => id !== songId)
      });
    }
  },

  reorderSetlistSongs: async (setlistId, fromIndex, toIndex) => {
    const setlist = get().setlists.find(s => s.id === setlistId);
    if (setlist) {
      const newSongs = [...setlist.songs];
      const [movedSong] = newSongs.splice(fromIndex, 1);
      newSongs.splice(toIndex, 0, movedSong);
      
      await get().updateSetlist(setlistId, { songs: newSongs });
    }
  },

  // Current Selection
  setCurrentSong: (song) => {
    set({ currentSong: song, selectedTransposition: 0 });
  },

  setCurrentSetlist: (setlist) => {
    set({ currentSetlist: setlist });
  },

  // Utility
  setError: (error) => {
    set({ error });
  },

  setLoading: (loading) => {
    set({ isLoading: loading });
  },

  exportSongs: async () => {
    const storage = getStorageService();
    const data = await storage.exportData();
    return exportSongsService(data.songs, data.setlists);
  },

  importSongs: async (data) => {
    const storage = getStorageService();
    const importData = importSongsFromJson(data);
    
    set({ isLoading: true, error: null });
    
    try {
      await storage.importData({
        songs: importData.songs,
        setlists: importData.setlists || []
      });
      // Reload songs after import
      await get().loadSongs();
      set({ isLoading: false });
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Import failed' 
      });
      throw error;
    }
  }
}));